import React from "react";
import clsx from "clsx";
import { makeStyles } from "@material-ui/core/styles";
import {
  Call,
  Check,
  FileCopyOutlined,
  OpenInNew,
  Reply,
  ShoppingCart,
  Storefront,
  ViewList,
} from "@material-ui/icons";

import MarkdownWrapper from "../MarkdownWrapper";
import { i18n } from "../../translate/i18n";

/**
 * Desenha o conteúdo de uma mensagem interativa (botões, lista, template,
 * carrossel ou enquete) a partir do envelope canônico que o backend grava em
 * `dataJson.interactive` (backend/src/utils/interactiveContent.ts).
 *
 * Sem `onReply`/`onVote` tudo é apenas exibido. Com `onReply`, botão de resposta
 * e linha de lista viram clicáveis e devolvem o próprio título — quem chama manda
 * esse texto como mensagem comum, pelo mesmo caminho do campo de texto. Com
 * `onVote`, a enquete aceita voto e devolve o estado final da escolha.
 *
 * CORES: as bolhas do chat têm fundo e texto FIXOS (messageLeft #ffffff /
 * messageRight #dcf8c6, ambos com texto #303030), sem seguir o tema. Usar
 * theme.palette aqui deixa tudo invisível no tema escuro — foi o que aconteceu na
 * primeira versão. Por isso o texto herda a cor da bolha (`color: inherit`) e os
 * tons secundários saem de rgba preto, que funciona sobre os dois fundos claros.
 */

// Verde do WhatsApp, legível tanto sobre o branco quanto sobre o verde da bolha.
const ACCENT = "#00a884";
const MUTED = "rgba(0, 0, 0, 0.55)";
const FAINT = "rgba(0, 0, 0, 0.42)";
const HAIRLINE = "rgba(0, 0, 0, 0.12)";
const SURFACE = "rgba(0, 0, 0, 0.05)";
const TRACK = "rgba(0, 0, 0, 0.10)";

const useStyles = makeStyles(() => ({
  container: {
    display: "flex",
    flexDirection: "column",
    color: "inherit",
    width: "100%",
  },
  header: {
    fontSize: 15,
    fontWeight: 600,
    color: "inherit",
    wordBreak: "break-word",
  },
  body: {
    fontSize: 15,
    color: "inherit",
    wordBreak: "break-word",
    whiteSpace: "pre-wrap",
  },
  footer: {
    fontSize: 12,
    color: MUTED,
    wordBreak: "break-word",
    marginTop: 2,
  },
  divider: {
    height: 1,
    backgroundColor: HAIRLINE,
    margin: "6px 0",
  },
  buttonList: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  // Chip inerte por padrão: sem cursor de clique e sem hover, para não sugerir uma
  // ação que o CRM não executa. O que responde ganha `buttonClickable` por cima.
  button: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    padding: "6px 10px",
    borderRadius: 6,
    border: `1px solid ${HAIRLINE}`,
    backgroundColor: SURFACE,
    color: ACCENT,
    fontSize: 13.5,
    lineHeight: 1.3,
    textAlign: "center",
    wordBreak: "break-word",
  },
  buttonClickable: {
    cursor: "pointer",
    userSelect: "none",
    "&:hover": {
      backgroundColor: TRACK,
    },
    "&:focus-visible": {
      outline: `2px solid ${ACCENT}`,
      outlineOffset: 1,
    },
  },
  buttonIcon: {
    fontSize: 15,
    flexShrink: 0,
  },
  buttonHint: {
    fontSize: 11,
    color: FAINT,
    marginLeft: 4,
    wordBreak: "break-all",
  },
  section: {
    marginTop: 6,
  },
  sectionTitle: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    color: MUTED,
    marginBottom: 2,
  },
  row: {
    padding: "4px 0",
    borderBottom: `1px solid ${HAIRLINE}`,
  },
  rowClickable: {
    cursor: "pointer",
    userSelect: "none",
    "&:hover": {
      backgroundColor: SURFACE,
    },
    "&:focus-visible": {
      outline: `2px solid ${ACCENT}`,
      outlineOffset: 1,
    },
  },
  rowTitle: {
    fontSize: 13.5,
    color: "inherit",
  },
  rowDescription: {
    fontSize: 12,
    color: MUTED,
  },

  // ----- Enquete (espelha a anatomia do WhatsApp Web) -----
  // Ocupa a bolha inteira, como o card de documento. O minWidth evita a enquete
  // espremida numa bolha estreita quando a pergunta e as opções são curtas.
  poll: {
    width: "100%",
    minWidth: 220,
  },
  pollQuestion: {
    fontSize: 15,
    fontWeight: 600,
    color: "inherit",
    wordBreak: "break-word",
    marginBottom: 10,
  },
  // Regra da enquete ("uma opção" / "uma ou mais"), colada na pergunta.
  pollHint: {
    fontSize: 12,
    color: MUTED,
    margin: "-6px 0 10px",
  },
  pollOption: {
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    marginBottom: 10,
    "&:last-child": {
      marginBottom: 2,
    },
  },
  pollOptionClickable: {
    cursor: "pointer",
    userSelect: "none",
    "&:focus-visible": {
      outline: `2px solid ${ACCENT}`,
      outlineOffset: 2,
    },
  },
  // Voto a caminho: trava os cliques até a enquete voltar atualizada.
  pollOptionBusy: {
    opacity: 0.6,
    pointerEvents: "none",
  },
  // Vazio até o próprio número votar; aí ganha `pollRadioChecked`.
  pollRadio: {
    width: 17,
    height: 17,
    borderRadius: "50%",
    border: `1.5px solid ${FAINT}`,
    flexShrink: 0,
    marginTop: 1,
  },
  // Enquete de várias opções usa caixa quadrada, como no WhatsApp.
  pollRadioSquare: {
    borderRadius: 4,
  },
  pollRadioChecked: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: ACCENT,
    borderColor: ACCENT,
  },
  pollRadioIcon: {
    fontSize: 13,
    color: "#ffffff",
  },
  pollOptionMain: {
    flex: 1,
    minWidth: 0,
  },
  pollOptionHeader: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: 8,
  },
  pollOptionLabel: {
    fontSize: 14,
    color: "inherit",
    wordBreak: "break-word",
  },
  pollOptionVotes: {
    fontSize: 13,
    color: MUTED,
    flexShrink: 0,
  },
  pollBar: {
    height: 4,
    borderRadius: 2,
    backgroundColor: TRACK,
    marginTop: 6,
    overflow: "hidden",
  },
  pollBarFill: {
    height: "100%",
    borderRadius: 2,
    backgroundColor: ACCENT,
  },

  // ----- Catálogo: produto e pedido -----
  // minWidth pelo mesmo motivo da enquete: sem ele o card fica espremido quando
  // o nome do produto é curto.
  catalog: {
    width: "100%",
    minWidth: 220,
  },
  productRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    padding: "6px 0",
    borderBottom: `1px solid ${HAIRLINE}`,
    "&:last-child": {
      borderBottom: "none",
    },
  },
  productThumb: {
    width: 52,
    height: 52,
    borderRadius: 6,
    objectFit: "cover",
    border: `1px solid ${HAIRLINE}`,
    flexShrink: 0,
    backgroundColor: SURFACE,
  },
  productInfo: {
    flex: 1,
    minWidth: 0,
  },
  productTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: "inherit",
    wordBreak: "break-word",
  },
  productDescription: {
    fontSize: 12,
    color: MUTED,
    wordBreak: "break-word",
    // Descrição de catálogo costuma ser longa; 2 linhas bastam no card.
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  productPrice: {
    fontSize: 13.5,
    fontWeight: 600,
    color: ACCENT,
    marginTop: 2,
  },
  productStrikePrice: {
    fontSize: 12,
    color: FAINT,
    textDecoration: "line-through",
    marginLeft: 6,
    fontWeight: 400,
  },
  productSku: {
    fontSize: 11,
    color: FAINT,
    wordBreak: "break-all",
  },
  productQuantity: {
    fontSize: 12,
    color: MUTED,
    flexShrink: 0,
    marginLeft: 4,
  },
  catalogHeading: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 13,
    fontWeight: 600,
    color: MUTED,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    marginBottom: 4,
  },
  catalogHeadingIcon: {
    fontSize: 16,
  },
  orderTotalRow: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: 8,
    marginTop: 6,
    fontSize: 14,
    fontWeight: 700,
    color: "inherit",
  },
  orderStatus: {
    fontSize: 12,
    color: MUTED,
    marginTop: 4,
  },

  replyChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    padding: "3px 8px",
    borderRadius: 6,
    backgroundColor: SURFACE,
    border: `1px solid ${HAIRLINE}`,
    fontSize: 13.5,
    color: "inherit",
    wordBreak: "break-word",
  },
  replyIcon: {
    fontSize: 14,
    color: ACCENT,
    flexShrink: 0,
  },
  formList: {
    marginTop: 6,
  },
  formEntry: {
    fontSize: 12,
    color: MUTED,
    wordBreak: "break-word",
  },
  cards: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    marginTop: 6,
  },
  card: {
    padding: 8,
    borderRadius: 6,
    border: `1px solid ${HAIRLINE}`,
  },
}));

const BUTTON_ICONS = {
  url: OpenInNew,
  call: Call,
  copy: FileCopyOutlined,
  reply: Reply,
};

const hostnameOf = (url) => {
  try {
    return new URL(url).hostname;
  } catch (_error) {
    return null;
  }
};

// Enter/Espaço acionam o chip, como em um <button> nativo.
const clickableProps = (onActivate) => ({
  role: "button",
  tabIndex: 0,
  onClick: onActivate,
  onKeyDown: (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onActivate();
    }
  },
});

const InteractiveButtons = ({ buttons, onReply }) => {
  const classes = useStyles();

  if (!buttons || buttons.length === 0) return null;

  return (
    <div className={classes.buttonList}>
      {buttons.map((button, index) => {
        const Icon = BUTTON_ICONS[button.type] || BUTTON_ICONS.reply;
        const hint =
          button.type === "url"
            ? hostnameOf(button.url)
            : button.type === "call"
            ? button.phoneNumber
            : button.type === "copy"
            ? button.copyCode
            : null;

        // Só botão de resposta responde; link, ligação e código seguem exibidos.
        const canReply =
          Boolean(onReply) &&
          Boolean(button.title) &&
          (!button.type || button.type === "reply");

        return (
          <div
            key={`${button.id || button.title}-${index}`}
            className={clsx(classes.button, canReply && classes.buttonClickable)}
            {...(canReply ? clickableProps(() => onReply(button.title)) : {})}
          >
            <Icon className={classes.buttonIcon} />
            <span>{button.title}</span>
            {hint && <span className={classes.buttonHint}>{hint}</span>}
          </div>
        );
      })}
    </div>
  );
};

const InteractiveSections = ({ sections, listButtonText, onReply }) => {
  const classes = useStyles();

  if (!sections || sections.length === 0) return null;

  return (
    <>
      <div className={classes.button}>
        <ViewList className={classes.buttonIcon} />
        <span>{listButtonText || i18n.t("messagesList.interactive.viewOptions")}</span>
      </div>
      {sections.map((section, sectionIndex) => (
        <div key={section.title || sectionIndex} className={classes.section}>
          {section.title && (
            <div className={classes.sectionTitle}>{section.title}</div>
          )}
          {(section.rows || []).map((row, rowIndex) => {
            const canReply = Boolean(onReply) && Boolean(row.title);

            return (
              <div
                key={row.id || `${rowIndex}`}
                className={clsx(classes.row, canReply && classes.rowClickable)}
                {...(canReply ? clickableProps(() => onReply(row.title)) : {})}
              >
                <div className={classes.rowTitle}>{row.title}</div>
                {row.description && (
                  <div className={classes.rowDescription}>{row.description}</div>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </>
  );
};

const InteractivePoll = ({ poll, onVote, busy }) => {
  const classes = useStyles();

  if (!poll) return null;

  // Regra da enquete: 1 = só uma opção; 0/ausente = quantas quiser.
  const single = (Number(poll.selectableCount) || 0) === 1;
  const ownVote = Array.isArray(poll.ownVote) ? poll.ownVote : [];

  // O voto enviado é sempre o estado final da escolha: clicar na opção já marcada
  // retira o voto e, em enquete de uma opção, clicar em outra troca o voto.
  const toggleOption = (option) => {
    if (busy) return;

    if (ownVote.includes(option)) {
      onVote(ownVote.filter((selected) => selected !== option));
    } else {
      onVote(single ? [option] : [...ownVote, option]);
    }
  };

  const votes = poll.votes || {};
  const totalVotes = Object.values(votes).reduce(
    (total, count) => total + (Number(count) || 0),
    0
  );

  // Contagem e barra só aparecem quando existe voto registrado: antes do primeiro
  // voto não há apuração, e mostrar "0" em toda opção afirmaria um resultado.
  const hasVotes = totalVotes > 0;

  return (
    <div className={classes.poll}>
      <div className={classes.pollQuestion}>
        {poll.question || i18n.t("messagesList.interactive.poll")}
      </div>
      <div className={classes.pollHint}>
        {i18n.t(
          single
            ? "messagesList.interactive.pollSelectOne"
            : "messagesList.interactive.pollSelectMany"
        )}
      </div>

      {(poll.options || []).map((option, index) => {
        const count = Number(votes[option]) || 0;
        const percentage = hasVotes ? (count / totalVotes) * 100 : 0;
        const checked = ownVote.includes(option);

        return (
          <div
            key={`${option}-${index}`}
            className={clsx(
              classes.pollOption,
              onVote && classes.pollOptionClickable,
              onVote && busy && classes.pollOptionBusy
            )}
            {...(onVote
              ? {
                  ...clickableProps(() => toggleOption(option)),
                  role: single ? "radio" : "checkbox",
                  "aria-checked": checked,
                }
              : {})}
          >
            <span
              className={clsx(
                classes.pollRadio,
                !single && classes.pollRadioSquare,
                checked && classes.pollRadioChecked
              )}
            >
              {checked && <Check className={classes.pollRadioIcon} />}
            </span>
            <div className={classes.pollOptionMain}>
              <div className={classes.pollOptionHeader}>
                <span className={classes.pollOptionLabel}>{option}</span>
                {hasVotes && (
                  <span className={classes.pollOptionVotes}>{count}</span>
                )}
              </div>
              {hasVotes && (
                <div className={classes.pollBar}>
                  <div
                    className={classes.pollBarFill}
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const InteractiveReply = ({ reply }) => {
  const classes = useStyles();

  if (!reply) return null;

  const formEntries = reply.form ? Object.entries(reply.form) : [];

  return (
    <div>
      <span className={classes.replyChip}>
        <Reply className={classes.replyIcon} />
        {reply.title}
      </span>
      {formEntries.length > 0 && (
        <div className={classes.formList}>
          {formEntries.map(([field, value]) => (
            <div key={field} className={classes.formEntry}>
              <strong>{field}:</strong>{" "}
              {typeof value === "object" ? JSON.stringify(value) : String(value)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Preço do catálogo trafega SEMPRE em centavos (o backend converte na borda de
 * cada canal). Moeda desconhecida pela Intl não pode derrubar a bolha.
 */
const formatPrice = (cents, currency) => {
  if (cents === null || cents === undefined || Number.isNaN(Number(cents))) {
    return null;
  }
  const value = Number(cents) / 100;
  try {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: currency || "BRL",
    }).format(value);
  } catch (e) {
    return `${value.toFixed(2)} ${currency || ""}`.trim();
  }
};

/** Miniatura do produto. URLs de CDN da Meta/WhatsApp expiram — some em vez de mostrar quebrada. */
const ProductThumb = ({ url, alt }) => {
  const classes = useStyles();

  if (!url) return null;

  return (
    <img
      className={classes.productThumb}
      src={url}
      alt={alt || ""}
      onError={(e) => {
        e.target.style.display = "none";
      }}
    />
  );
};

const ProductRow = ({ product }) => {
  const classes = useStyles();

  const price = formatPrice(product.priceCents, product.currency);
  const salePrice = formatPrice(product.salePriceCents, product.currency);
  const quantity = Number(product.quantity) || 0;

  return (
    <div className={classes.productRow}>
      <ProductThumb url={product.imageUrl} alt={product.title} />
      <div className={classes.productInfo}>
        <div className={classes.productTitle}>
          {product.title || i18n.t("messagesList.catalog.unnamedProduct")}
        </div>
        {product.description && (
          <div className={classes.productDescription}>{product.description}</div>
        )}
        {product.retailerId && (
          <div className={classes.productSku}>
            {i18n.t("messagesList.catalog.sku")}: {product.retailerId}
          </div>
        )}
        {price && (
          <div className={classes.productPrice}>
            {/* Em promoção o preço vigente é o salePrice; o cheio fica riscado. */}
            {salePrice || price}
            {salePrice && <span className={classes.productStrikePrice}>{price}</span>}
          </div>
        )}
      </div>
      {quantity > 1 && (
        <div className={classes.productQuantity}>&times;{quantity}</div>
      )}
    </div>
  );
};

const InteractiveProducts = ({ header, text, footer, products }) => {
  const classes = useStyles();

  const items = Array.isArray(products) ? products : [];

  return (
    <div className={classes.catalog}>
      <div className={classes.catalogHeading}>
        <Storefront className={classes.catalogHeadingIcon} />
        {items.length > 1
          ? i18n.t("messagesList.catalog.productsTitle")
          : i18n.t("messagesList.catalog.productTitle")}
      </div>
      {header && (
        <div className={classes.header}>
          <MarkdownWrapper>{header}</MarkdownWrapper>
        </div>
      )}
      {items.map((product, index) => (
        <ProductRow key={product.retailerId || product.productId || index} product={product} />
      ))}
      {text && (
        <div className={classes.body}>
          <MarkdownWrapper>{text}</MarkdownWrapper>
        </div>
      )}
      {footer && (
        <div className={classes.footer}>
          <MarkdownWrapper>{footer}</MarkdownWrapper>
        </div>
      )}
    </div>
  );
};

const InteractiveOrder = ({ order, products, text }) => {
  const classes = useStyles();

  const items = Array.isArray(products) ? products : [];
  const total = formatPrice(order?.totalCents, order?.currency);
  const status = order?.detailsStatus;

  return (
    <div className={classes.catalog}>
      <div className={classes.catalogHeading}>
        <ShoppingCart className={classes.catalogHeadingIcon} />
        {i18n.t("messagesList.catalog.orderTitle")}
      </div>

      {items.map((product, index) => (
        <ProductRow key={product.retailerId || product.productId || index} product={product} />
      ))}

      {/* No Baileys os itens chegam num segundo passo; enquanto isso a bolha
          mostra só os totais, com aviso de que o resto está a caminho. */}
      {items.length === 0 && status === "pending" && (
        <div className={classes.orderStatus}>
          {i18n.t("messagesList.catalog.orderLoading")}
        </div>
      )}
      {items.length === 0 && status === "failed" && (
        <div className={classes.orderStatus}>
          {i18n.t("messagesList.catalog.orderFailed")}
        </div>
      )}

      {total && (
        <>
          <div className={classes.divider} />
          <div className={classes.orderTotalRow}>
            <span>{i18n.t("messagesList.catalog.orderTotal")}</span>
            <span>{total}</span>
          </div>
        </>
      )}

      {text && (
        <div className={classes.body}>
          <MarkdownWrapper>{text}</MarkdownWrapper>
        </div>
      )}
    </div>
  );
};

const InteractiveMessageContent = ({ interactive, onReply, onVote, busy }) => {
  const classes = useStyles();

  if (!interactive || typeof interactive !== "object") return null;

  if (interactive.kind === "reply") {
    return <InteractiveReply reply={interactive.reply} />;
  }

  if (interactive.kind === "poll") {
    return <InteractivePoll poll={interactive.poll} onVote={onVote} busy={busy} />;
  }

  if (interactive.kind === "product") {
    return (
      <InteractiveProducts
        header={interactive.header}
        text={interactive.text}
        footer={interactive.footer}
        products={interactive.products}
      />
    );
  }

  if (interactive.kind === "order") {
    return (
      <InteractiveOrder
        order={interactive.order}
        products={interactive.products}
        text={interactive.text}
      />
    );
  }

  const hasButtons = interactive.buttons?.length > 0;
  const hasSections = interactive.sections?.length > 0;
  const hasCards = interactive.cards?.length > 0;
  const hasText = Boolean(
    interactive.header || interactive.text || interactive.footer
  );

  if (!hasButtons && !hasSections && !hasCards && !hasText) return null;

  return (
    <div className={classes.container}>
      {interactive.header && (
        <div className={classes.header}>
          <MarkdownWrapper>{interactive.header}</MarkdownWrapper>
        </div>
      )}
      {interactive.text && (
        <div className={classes.body}>
          <MarkdownWrapper codeBlocks>{interactive.text}</MarkdownWrapper>
        </div>
      )}
      {interactive.footer && (
        <div className={classes.footer}>
          <MarkdownWrapper>{interactive.footer}</MarkdownWrapper>
        </div>
      )}

      {hasCards && (
        <div className={classes.cards}>
          {interactive.cards.map((card, index) => (
            <div key={index} className={classes.card}>
              {card.header && (
                <div className={classes.header}>
                  <MarkdownWrapper>{card.header}</MarkdownWrapper>
                </div>
              )}
              {card.text && (
                <div className={classes.body}>
                  <MarkdownWrapper>{card.text}</MarkdownWrapper>
                </div>
              )}
              {card.footer && (
                <div className={classes.footer}>
                  <MarkdownWrapper>{card.footer}</MarkdownWrapper>
                </div>
              )}
              {card.buttons?.length > 0 && (
                <>
                  <div className={classes.divider} />
                  <InteractiveButtons buttons={card.buttons} onReply={onReply} />
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {(hasButtons || hasSections) && hasText && (
        <div className={classes.divider} />
      )}
      {hasButtons && (
        <InteractiveButtons buttons={interactive.buttons} onReply={onReply} />
      )}
      {hasSections && (
        <InteractiveSections
          sections={interactive.sections}
          listButtonText={interactive.listButtonText}
          onReply={onReply}
        />
      )}
    </div>
  );
};

export default InteractiveMessageContent;
